export enum UserRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  EDITOR = 'EDITOR',
  VIEWER = 'VIEWER',
  SOCIO = 'SOCIO'
}

export enum AppView {
  PIPELINE = 'Pipeline',
  CLIENTS = 'Clientes y proyectos',
  WEEKLY_METRICS = 'Métricas semanales',
  CASH = 'Caja -> 3.000€',
  DASHBOARD = 'Dashboard',
  CRM = 'CRM',
  CONTENT = 'Contenidos',
  PLANNING = 'Planificación',
  PROJECTS = 'Proyectos',
  DOCUMENTS = 'Ventas y Docs',
  KPIS = 'KPIs',
  SETTINGS = 'Configuración'
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
}

export interface Workspace {
  id: string;
  name: string;
  role: UserRole;
}

export interface DashboardStats {
  newLeads: number;
  interactions: number;
  pendingToday: number;
  postsCreated: number;
  postsPublished: number;
  blocksCompletedPercent: number;
}

export enum BlockStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED'
}

export enum BlockCategory {
  DEEP_WORK = 'DEEP_WORK',
  SALES = 'SALES',
  MEETING = 'MEETING',
  ADMIN = 'ADMIN',
  CONTENT = 'CONTENT',
  LEARNING = 'LEARNING',
  OTHER = 'OTHER'
}

export interface WeeklyBlockTemplate {
  id: string;
  workspaceId: string;
  dayIndex: number; // 0 = Monday, 6 = Sunday
  title: string;
  category: BlockCategory;
  startTime: string; // "HH:MM"
  endTime: string; // "HH:MM"
  autoCreateTasks: boolean;
}

export interface WeeklyBlockInstance extends WeeklyBlockTemplate {
  templateId: string;
  date: string; // YYYY-MM-DD
  status: BlockStatus;
  notes?: string;
}

export enum LeadStage {
  NEW = 'NEW',
  EMAIL_DRAFT = 'EMAIL_DRAFT',
  CONTACTED = 'CONTACTED',
  MEETING = 'MEETING',
  INTERESTED = 'INTERESTED',
  PROPOSAL = 'PROPOSAL',
  NEGOTIATION = 'NEGOTIATION',
  CLOSED_WON = 'CLOSED_WON',
  CLOSED_LOST = 'CLOSED_LOST'
}

export type LeadCanal = 'COLD_EMAIL' | 'LINKEDIN' | 'PHONE';

export interface Company {
  id: string;
  workspaceId: string;
  name: string;
  sector: string;
  stage: LeadStage;
  score: number; // 0-10
  nextContactAt?: string;
  createdAt: string;
  followUpAttempts: number;
  ownerId: string;
  website?: string;
  notes?: string;
  canal?: LeadCanal;
  // LinkedIn & prospecting fields
  linkedinUrl?: string;
  region?: string;
  employeeCount?: string;
  source?: string;
}

export interface Contact {
  id: string;
  companyId: string;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  isPrimary: boolean;
  // LinkedIn & prospecting fields
  linkedinUrl?: string;
  linkedinStatus?: 'TO_FOLLOW' | 'FOLLOWING' | 'CONNECTION_SENT' | 'CONNECTED' | 'ENGAGED';
  notes?: string;
}

export interface WeeklyMetric {
  id?: string;
  weekStart: string;
  empresasContactadas: number;
  conversaciones: number;
  posts: number;
  entrenoDias: number;
  movilHoras: number;
  autovaloracion: number;
  diagnosticos: number;
  retainers: number;
  notas?: string;
}

export interface CashLedgerEntry {
  id?: string;
  date: string;
  concepto: string;
  companyId?: string;
  importe: number;
  tipo: 'FACTURADO' | 'COBRADO' | 'PREVISTO';
  cobrado: boolean;
  notas?: string;
}

export interface Interaction {
  id: string;
  companyId: string;
  type: 'CALL' | 'EMAIL' | 'MEETING' | 'LINKEDIN';
  notes: string;
  date: string;
  sentiment?: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
  nextSteps?: string;
}

export enum TaskPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT'
}

export enum TaskStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING = 'WAITING',
  DONE = 'DONE'
}

export interface Task {
  id: string;
  workspaceId: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate?: string;
  relatedCompanyId?: string;
  relatedProjectId?: string;
  assigneeId?: string;
}

export enum ContentPlatform {
  LINKEDIN = 'LINKEDIN',
  INSTAGRAM = 'INSTAGRAM',
  TIKTOK = 'TIKTOK',
  YOUTUBE = 'YOUTUBE',
  TWITTER = 'TWITTER',
  BLOG = 'BLOG',
  NEWSLETTER = 'NEWSLETTER'
}

export enum ContentFormat {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  CAROUSEL = 'CAROUSEL',
  ARTICLE = 'ARTICLE'
}

export enum ContentStatus {
  IDEA = 'IDEA',
  DRAFTING = 'DRAFTING',
  REVIEW = 'REVIEW',
  SCHEDULED = 'SCHEDULED',
  PUBLISHED = 'PUBLISHED'
}

export interface ContentIdea {
  id: string;
  title: string;
  description: string;
  tags: string[];
}

export interface ContentPost {
  id: string;
  workspaceId: string;
  title: string;
  platforms: ContentPlatform[];
  format: ContentFormat;
  status: ContentStatus;
  scheduledFor?: string;
  content?: string;
  /** Guion del reel / vídeo. Solo se usa cuando format es VIDEO. */
  script?: string;
  mediaUrl?: string;
  ideaId?: string;
  notes?: string;
}

export enum AgendaEventType {
  MEETING = 'MEETING',
  BLOCK = 'BLOCK',
  TASK = 'TASK',
  REMINDER = 'REMINDER'
}

export interface AgendaEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: AgendaEventType;
  description?: string;
  attendees?: string[];
  location?: string;
}

export enum ProjectStatus {
  PLANNING = 'PLANNING',
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED'
}

export interface ProjectPart {
  id: string;
  projectId: string;
  name: string;
  progress: number; // 0-100
}

export interface ProjectMeeting {
  id: string;
  projectId: string;
  title: string;
  date: string;
  notes?: string;
}

export interface Project {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  status: ProjectStatus;
  startDate: string;
  endDate?: string;
  clientCompanyId?: string;
  progress?: number;
}

export enum ServiceType {
  AUTOMATION = 'AUTOMATION',
  AI_IMPLEMENTATION = 'AI_IMPLEMENTATION',
  TRAINING = 'TRAINING',
  CONSULTING = 'CONSULTING'
}

export interface TrainingModule {
  id: string;
  title: string;
  duration: number; // minutes
  type: 'THEORY' | 'PRACTICE';
  tags: string[];
  url?: string;
}

export interface Course {
  id: string;
  title: string;
  description: string;
  modules: TrainingModule[];
}

export enum CourseAudience {
  INTERNAL = 'INTERNAL',
  CLIENT = 'CLIENT',
  PUBLIC = 'PUBLIC'
}

export interface CourseEdition {
  id: string;
  courseId: string;
  startDate: string;
  audience: CourseAudience;
  attendeesCount: number;
}

export enum DocumentType {
  PROPOSAL = 'PROPOSAL',
  CONTRACT = 'CONTRACT',
  REPORT = 'REPORT',
  INVOICE = 'INVOICE',
  OTHER = 'OTHER'
}

export interface DocumentLink {
  id: string;
  workspaceId: string;
  title: string;
  type: DocumentType;
  url: string;
  createdAt: string;
  relatedCompanyId?: string;
}

export interface ServiceItem {
  id: string;
  name: string;
  description: string;
  price: number;
  isRecurring: boolean;
  category: string;
}

export interface Proposal {
  id: string;
  companyId: string;
  date: string;
  items: { serviceId: string; quantity: number; customPrice?: number }[];
  total: number;
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED';
}

export interface LibraryResource {
  id: string;
  title: string;
  type: 'PROMPT' | 'TEMPLATE' | 'GUIDE';
  content: string;
  tags: string[];
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  read: boolean;
  createdAt: string;
  linkTo?: AppView;
}

export interface ActivityLog {
  id: string;
  userId: string;
  action: string;
  entity: string;
  entityName: string;
  timestamp: string;
}
